import apiClient from './authClient';
import { API_ENDPOINTS } from './authConfig';
import type { User, UserUpdate, UserDeleteRequest } from '../../types/auth';

/**
 * User management API endpoints
 * These are separate from authentication endpoints for better organization
 */
export const usersApi = {
  // Get current user profile (alias for auth.getCurrentUser)
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get(API_ENDPOINTS.USERS.ME);
    return response.data;
  },

  // Update current user profile
  updateCurrentUser: async (userData: Partial<UserUpdate>): Promise<User> => {
    const response = await apiClient.patch(API_ENDPOINTS.USERS.ME, userData);
    return response.data;
  },

  // Get user by ID (admin only)
  getUserById: async (id: string): Promise<User> => {
    const response = await apiClient.get(API_ENDPOINTS.USERS.BY_ID(id));
    return response.data;
  },

  // Update user by ID (admin only)
  updateUserById: async (id: string, userData: Partial<UserUpdate>): Promise<User> => {
    const response = await apiClient.patch(API_ENDPOINTS.USERS.BY_ID(id), userData);
    return response.data;
  },

  // Delete user by ID (admin only)
  deleteUserById: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.USERS.BY_ID(id));
  },

  // Delete current user account
  deleteCurrentUser: async (password: string): Promise<void> => {
    const deleteRequest: UserDeleteRequest = { password };
    await apiClient.delete(API_ENDPOINTS.USERS.DELETE, {
      data: deleteRequest,
    });
  },

  // Change password with current password verification
  changePassword: async (currentPassword: string, newPassword: string): Promise<User> => {
    const response = await apiClient.patch(API_ENDPOINTS.USERS.ME, {
      password: newPassword,
      // Note: In a real implementation, you might need to verify current password
      // through a separate endpoint or include it in the request body
      // For now, we'll include it in the request for backend validation
      current_password: currentPassword,
    });
    return response.data;
  },
};
